import { CloseCircleFilled } from "@ant-design/icons";
import MiniSpinner from "@Components/shared/MiniSpinner";
import SessionManager from "@Core/session";
import { IAssessmentDetails } from "@Models/IAssessmentDetails";
import AssessmentService from "@Services/AssessmentService";
import { DateTime, IColumnData, useFilter, useSearchParams } from "@Utils";
import { Button, Empty, notification, Popconfirm } from "antd";
import React from "react";
import { IUseAssessmentData } from "../types";
import { useAssessmentsQuery, useTemplatesMap } from "./../queries";
import TickIcon from "@Components/shared/TickIcon";
import CrossIcon from "@Components/shared/CrossIcon";

interface IUseCurrentAssessmentsData extends IUseAssessmentData<IAssessmentDetails> {
    assessments: IAssessmentDetails[];
    filterText: string;
    tableLocale: { emptyText: JSX.Element };

    onRowClick: (assessmentId: IAssessmentDetails["assessmentId"]) => void;
}

const useCurrentAssessments = (): IUseCurrentAssessmentsData => {
    const user = SessionManager.getServiceUser()?.userId;
    const { filterText, onFilter } = useFilter();
    const [, setSearchParams] = useSearchParams();

    const checkIfOverdue = (assessment: IAssessmentDetails): boolean => {
        const lateSubmissions = assessment.lateSubmissions || 0;
        const endDate = DateTime(assessment.endDate).addDays(lateSubmissions);

        return DateTime(endDate).isPast();
    };

    const { data: assessments, isFetching, isLoading, refetch: refetchAssessmentList } = useAssessmentsQuery(user);
    const { data: templatesMap, isFetched: isTemplatesMapFetched } = useTemplatesMap();

    const deleteAssessment = async (selectedAssessment: string) => {
        // TODO: this should be a mutation that invalidates the assessmentlist
        const service = new AssessmentService();

        if (!user || !selectedAssessment) return;

        await service
            .delete(selectedAssessment)
            .then((res) => {
                if (!res.errors.length) {
                    if (res.value) refetchAssessmentList();
                } else {
                    notification.error({ message: "Something went wrong." });
                }
            })
            .catch(() => {
                notification.error({ message: "Something went wrong." });
            });
    };

    const handleDelete = async ({ assessmentId }: IAssessmentDetails) => await deleteAssessment(assessmentId);
    const handleRowClick = async (assessmentId: IAssessmentDetails["assessmentId"]) =>
        setSearchParams({ tab: "users", assessment: assessmentId });

    const columnData: IColumnData<IAssessmentDetails>[] = [
        { key: "name", align: "left", sorter: (a, b) => a.name.localeCompare(b.name) },
        { key: "description", align: "left" },
        { key: "tasks", align: "center", render: (tasks: object[]) => tasks.length },
        {
            key: "feedbackTemplateId",
            title: "Template",
            align: "center",
            render: (templateId: IAssessmentDetails["feedbackTemplateId"]) =>
                isTemplatesMapFetched ? (
                    templatesMap.get(templateId)?.name
                ) : (
                    <MiniSpinner style={{ background: "none" }} />
                ),
        },
        { key: "startDate", render: "date", align: "center", sorter: (a, b) => a.startDate.localeCompare(b.startDate) },
        { key: "endDate", render: "date", align: "center", sorter: (a, b) => a.endDate.localeCompare(b.endDate) },
        {
            className: "no-padding action-cell",
            key: "endDate",
            title: "Ended",
            align: "center",
            render: (_, datum) => {
                return checkIfOverdue(datum) ? <TickIcon /> : <CrossIcon />;
            },
        },
        {
            className: "no-padding action-cell",
            key: "delete",
            align: "center",
            onCell: () => ({
                onClick: (event) => event.stopPropagation(),
            }),
            title: "",
            render: (_, record) => (
                <Popconfirm
                    placement="leftBottom"
                    title="Deleting this assessment will remove all tasks and user submissions. Are you sure you want to do this?"
                    onConfirm={() => handleDelete(record)}
                    cancelButtonProps={{ className: "secondary" }}
                >
                    <CloseCircleFilled style={{ color: "#888" }} />
                </Popconfirm>
            ),
        },
    ];

    const filterAssessments = (unfilteredAssessments: IAssessmentDetails[]) => {
        if (!filterText) return unfilteredAssessments;
        return unfilteredAssessments.filter(
            (assessment) =>
                assessment.name.toLowerCase().includes(filterText) ||
                assessment.description.toLowerCase().includes(filterText)
        );
    };

    const filteredAssessments = filterAssessments(assessments);

    const tableLocale = {
        emptyText: (
            <>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="You don't have any assessments." />
                <Button className="secondary" onClick={() => setSearchParams({ tab: "new" })}>
                    Create a New Assessment
                </Button>
            </>
        ),
    };

    return {
        assessments: filteredAssessments,
        columnData: columnData,
        filterText: filterText,
        isFetching,
        isLoading,
        tableLocale,
        onFilter: onFilter,
        onReload: refetchAssessmentList,
        onRowClick: handleRowClick,
    };
};

export default useCurrentAssessments;
